import React, { useMemo, useRef } from "react";
import {
  FormControl,
  TextField as MuiTextField,
} from "@material-ui/core";
import clsx from "clsx";
import {TextFieldSize } from "./tools/TextField.const";
import { TextFieldModel } from "./tools/TextField.model";
import { useTelInputValidation } from "./tools/TextField.hooks";
import { useUniqueWcagValues, useFocusVisibleRef, mergeRefs } from "common-react";
import { FieldLabel } from "../FieldLabel/FieldLabel";
import { FieldErrorMessage } from "../FieldErrorMessage/FieldErrorMessage";
import { FieldHelperText } from "../FieldHelperText/FieldHelperText";
import { TextFieldAdornment } from "./components/TextFieldAdornment/TextFieldAdornment";
import { mergeAriaDescribedBy } from "../../utils/ariaUtils";

import useStyles from "./TextField.style";

export const TextField = ({
  className,
  required = false,
  size = TextFieldSize.DEFAULT,
  label,
  placeholder,
  name,
  type,
  id,
  errorId,
  autoComplete,
  autoFocus = false,
  testId = "TextField",
  onChange,
  inputProps,
  error,
  hasError = false,
  helperText,
  helperTextPosition = 'top',
  helperTextClassName,
  InputProps,
  startAdornment,
  endAdornment,
  fullWidth = false,
  defaultValue,
  value,
  inputRef,
  disabled = false,
  inputMode = 'text',
  autoTestId,
  ...props
}: TextFieldModel) => {
  const { getUniqueValue } = useUniqueWcagValues();
  const fieldRef = useRef<HTMLDivElement>(null);
  const uniqueId = getUniqueValue(name || "");
  const uniqueErrorId = getUniqueValue(`${name}-error`);
  const { isFocusVisible, ref } = useFocusVisibleRef();
  const c = useStyles({ size, fullWidth, helperTextPosition });

  useTelInputValidation(fieldRef, type);

  const errorOccurred = useMemo(() => {
    return !!error || hasError;
  }, [error, hasError]);

  const helperTextElement = helperText ? (
    <FieldHelperText className={helperTextClassName} disabled={disabled}>{helperText}</FieldHelperText>
  ) : null;

  return (
    <FormControl
      className={clsx(c.root, className, 'TextField')}
      data-testid={testId}
      data-type="ui/TextField"
    >
      <FieldLabel htmlFor={id || uniqueId} testId={`${testId}-label`}>
        {label}
      </FieldLabel>
      {helperTextPosition === 'top' && helperTextElement}
      <MuiTextField
        {...props}
        required={required}
        ref={inputRef}
        variant="outlined"
        placeholder={placeholder}
        name={name}
        type={type}
        id={id || uniqueId}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={onChange}
        disabled={disabled}
        defaultValue={defaultValue}
        value={value}
        error={errorOccurred}
        inputRef={mergeRefs(ref, inputRef)}
        InputProps={{
          ...InputProps,
          className: clsx(isFocusVisible && 'Mui-focusVisible'),
          startAdornment: <TextFieldAdornment position="start" disabled={disabled}>{startAdornment}</TextFieldAdornment>,
          endAdornment: <TextFieldAdornment position="end" disabled={disabled}>{endAdornment}</TextFieldAdornment>,
          ...(autoTestId ? { 'data-autotestid': autoTestId } : {}),
        }}
        inputProps={{
          ...inputProps,
          required: false,
          tabIndex: 0,
          "aria-required": required,
          "aria-describedby": mergeAriaDescribedBy(
            inputProps?.['aria-describedby'],
            error || hasError ? errorId || uniqueErrorId : undefined,
          ),
          "data-testid": `${testId}-input`,
          inputMode,
        }}
      />
      {helperTextPosition === 'bottom' && helperTextElement}
      {error && (
        <FieldErrorMessage id={errorId || uniqueErrorId}>{error}</FieldErrorMessage>
      )}
    </FormControl>
  );
};
